import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, LineChart, Line } from "recharts";
import { Factory, Boxes, Fuel, UserCheck, Share2 } from "lucide-react";
import { toast } from "sonner";

const CHART_COLORS = ["#2A2A28", "#5B7B61", "#C85A42", "#8E8E8A", "#D8D8D5"];

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-5 border border-[#D8D8D5] shadow-none rounded-md hover:-translate-y-0.5 transition-transform rise">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">{label}</div>
        <Icon className="w-4 h-4 text-[#6E6E6A]" />
      </div>
      <div className="font-display text-3xl font-black tracking-tighter mt-2 text-[#1C1C1A]">{value}</div>
      {sub && <div className="text-xs text-[#6E6E6A] mt-1">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then(r => setData(r.data)).catch(() => {});
  }, []);

  const shareWhatsApp = async () => {
    try {
      const { data: s } = await api.get("/reports/daily-summary");
      const url = `https://wa.me/?text=${encodeURIComponent(s.text)}`;
      window.open(url, "_blank");
    } catch { toast.error("Could not generate summary"); }
  };

  if (!data) return <div className="p-8 text-[#6E6E6A]">Loading…</div>;

  const prodByCat = Object.entries(data.week.production_by_category || {}).map(([k, v]) => ({ name: k, value: v }));
  const matByType = Object.entries(data.week.material_by_type || {}).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="p-4 sm:p-8 grain-bg">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Overview</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-black text-[#1C1C1A]">Dashboard</h1>
            <p className="text-[#6E6E6A] mt-2 text-sm">Live operations snapshot · updated on load</p>
          </div>
          <Button onClick={shareWhatsApp} data-testid="share-whatsapp" className="gap-2 bg-[#25D366] hover:bg-[#1EBE5A] text-white">
            <Share2 className="w-4 h-4" /> Share daily on WhatsApp
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Stat icon={Factory} label="Today's Production" value={data.today.production_total.toFixed(0)} sub="units produced" />
          <Stat icon={UserCheck} label="Present Today" value={data.today.attendance_count} sub="workers on shift" />
          <Stat icon={Fuel} label="Genset · 7 days" value={`${data.week.genset_hours.toFixed(1)}h`} sub={`${data.week.genset_fuel.toFixed(1)} L fuel`} />
          <Stat icon={Boxes} label="Material types · 7d" value={Object.keys(data.week.material_by_type || {}).length} sub="in circulation" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <Card className="lg:col-span-2 p-6 border border-[#D8D8D5] shadow-none rounded-md rise">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Production trend · 7 days</div>
            <div className="h-72 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.trend_7d}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E6" />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#6E6E6A" fontSize={11} />
                  <YAxis stroke="#6E6E6A" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D8D8D5", borderRadius: 4 }} />
                  <Line type="monotone" dataKey="quantity" stroke="#1C1C1A" strokeWidth={2} dot={{ r: 3, fill: "#C85A42" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6 border border-[#D8D8D5] shadow-none rounded-md rise">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">By category · 7 days</div>
            <div className="h-72 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={prodByCat}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E6" />
                  <XAxis dataKey="name" stroke="#6E6E6A" fontSize={10} />
                  <YAxis stroke="#6E6E6A" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D8D8D5", borderRadius: 4 }} />
                  <Bar dataKey="value" fill="#5B7B61" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <Card className="p-6 border border-[#D8D8D5] shadow-none rounded-md mt-4 rise">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Raw material consumption · 7 days</div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={matByType} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E6" />
                <XAxis type="number" stroke="#6E6E6A" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#6E6E6A" fontSize={11} width={130} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D8D8D5", borderRadius: 4 }} />
                <Bar dataKey="value" fill="#C85A42" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
